import React, { useState, useEffect } from "react";
import { isPSSOutdated } from "../../components/common/PSSUpdateBanner";
import { getLatestPSSVersion } from "../../services/configService";

const DOWNLOAD_WINDOWS = "/downloads/Party%20Sharing%20Studio%20Setup%201.1.0.rar?v=20260511-1";
const DOWNLOAD_MAC     = "/downloads/Party%20Sharing%20Studio-1.1.0.dmg?v=20260511-1";

function detectOS() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (ua.includes("mac")) return "mac";
  return "windows";
}

const CompanionInstall = () => {
  const [status, setStatus] = useState("checking");
  const [installedVersion, setInstalledVersion] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [latestDownloads, setLatestDownloads] = useState({ windows: DOWNLOAD_WINDOWS, mac: DOWNLOAD_MAC });
  const os = detectOS();

  const fetchLatestVersion = () => {
    getLatestPSSVersion()
      .then((res) => {
        const data = res.data;
        if (data?.version) setLatestVersion(data.version);
        if (data?.windows || data?.mac) {
          setLatestDownloads({
            windows: data.windows || DOWNLOAD_WINDOWS,
            mac: data.mac || DOWNLOAD_MAC,
          });
        }
      })
      .catch(() => { /* silently ignore — we still show current downloads */ });
  };

  useEffect(() => {
    fetchLatestVersion();
    import("../../services/videoCompressionService").then((mod) => {
      mod._resetCompanionStatus?.();
      mod.checkCompanion().then((ok) => {
        setStatus(ok ? "running" : "not-running");
        setInstalledVersion(mod.getCompanionVersion?.() ?? null);
      });
    });
  }, []);

  const recheckStatus = () => {
    setStatus("checking");
    setInstalledVersion(null);
    import("../../services/videoCompressionService").then((mod) => {
      mod._resetCompanionStatus?.();
      mod.checkCompanion().then((ok) => {
        setStatus(ok ? "running" : "not-running");
        setInstalledVersion(mod.getCompanionVersion?.() ?? null);
      });
    });
  };

  const isOutdated = status === "running" && isPSSOutdated(installedVersion, latestVersion);

  const statusConfig = {
    checking:      { dot: "⏳", label: "Checking Studio…",         sub: "Probing localhost:7891…",                                                                      bg: "rgba(255,255,255,0.9)", border: "rgba(0,0,0,0.15)",  labelColor: "#000",     dotBg: "#9ca3af" },
    running:       { dot: "●",  label: isOutdated ? "Studio is running — update available" : "Studio is running",        sub: isOutdated ? `Installed v${installedVersion} — latest is v${latestVersion}. Update before uploading.` : `Running v${installedVersion || "—"} — All video uploads will use native FFmpeg — fast, local, full quality.`,  bg: isOutdated ? "rgba(254,243,199,0.95)" : "rgba(220,252,231,0.95)",  border: isOutdated ? "rgba(251,191,36,0.5)" : "rgba(34,197,94,0.35)",   labelColor: "#000",  dotBg: isOutdated ? "#b45309" : "#15803d" },
    "not-running": { dot: "●",  label: "Studio is not running",    sub: "Videos over 500 MB will be blocked. Install below to enable full-size video compression.",    bg: "rgba(254,242,242,0.95)",  border: "rgba(239,68,68,0.35)",   labelColor: "#000",  dotBg: "#b91c1c" },
  };
  const sc = statusConfig[status];

  const macSteps = [
    { n: 1, label: "Download the installer",           action: <a href={latestDownloads.mac} download style={styles.downloadBtn}>⬇&nbsp; Download Studio (.dmg)</a> },
    { n: 2, label: "Open the DMG → drag to Applications", action: <span style={styles.stepNote}>Double-click the .dmg, then drag PSA Studio into your Applications folder.</span> },
    { n: 3, label: "Launch from Applications",         action: <span style={styles.stepNote}>Open from Applications or Spotlight. It appears silently in the menu bar and is ready immediately.</span> },
  ];
  const winSteps = [
    { n: 1, label: "Download the installer",           action: <a href={latestDownloads.windows} download style={styles.downloadBtn}>⬇&nbsp; Download Studio (.rar)</a> },
    { n: 2, label: "Extract and run the setup",        action: <span style={styles.stepNote}>Extract the RAR, then double-click the .exe inside — one-click install, no options needed.</span> },
    { n: 3, label: "It starts automatically",          action: <span style={styles.stepNote}>The app appears silently in your system tray and is ready to use immediately.</span> },
  ];
  const steps = os === "mac" ? macSteps : winSteps;

  return (
    <div style={styles.page}>

      {/* ── Hero ── */}
      <div style={styles.hero}>
        <div style={styles.heroLeft}>
          <div style={styles.heroIconWrap}>🖥️</div>
          <div>
            <h2 style={styles.heroTitle}>PSA Studio</h2>
            <p style={styles.heroSub}>
              PSA Studio is a desktop upload tool for the Promoter Panel. It helps compress selected photos and videos locally on your computer, apply configured watermarks and upload the processed media to the selected event or gallery.
            </p>
            <div style={styles.bannerCtaWrap}>
              <a href={os === "mac" ? latestDownloads.mac : latestDownloads.windows} download style={styles.primaryDownloadBtn}>
                Download PSA Studio
              </a>
              <span style={styles.downloadNote}>
                By downloading and using PSA Studio, you agree to the PSA Studio Terms of Use and acknowledge the Promoter Panel Privacy Policy.
              </span>
              <span style={styles.ffmpegNote}>
                PSA Studio uses FFmpeg for media processing, compression and conversion. FFmpeg license information, notices and source code information are available under “Open Source Licenses”.
              </span>
            </div>
            {/* Status pill inside hero */}
            <div style={{ ...styles.statusPill, background: sc.bg, borderColor: sc.border }}>
              <span style={{ ...styles.statusDotCircle, background: sc.dotBg, color: sc.labelColor }}>{sc.dot}</span>
              <span style={{ ...styles.statusLabel, color: sc.labelColor }}>{sc.label}</span>
              <span style={styles.statusSub}>{sc.sub}</span>
              <button style={styles.recheckBtn} onClick={recheckStatus}>↺ Recheck</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Version update banner ── */}
      {isOutdated && (
        <div style={styles.updateBanner}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>⚠️</span>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
            <strong style={{ fontSize: 13.5, fontWeight: 700, color: "#92400e" }}>
              Update required before uploading
            </strong>
            <span style={{ fontSize: 13, color: "#78350f", lineHeight: 1.55 }}>
              You have <strong>v{installedVersion}</strong> but the latest is{" "}
              <strong>v{latestVersion}</strong>. Download the new installer below,
              run it, and then click <strong>↺ Recheck</strong> to confirm.
            </span>
          </div>
          <a
            href={os === "mac" ? latestDownloads.mac : latestDownloads.windows}
            download
            style={styles.updateBtn}
          >
            ⬇&nbsp; Download v{latestVersion}
          </a>
        </div>
      )}

      {/* ── Install Steps (TOP priority) ── */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionBadge}>QUICK START</span>
          <h3 style={styles.sectionTitle}>Install in 3 steps</h3>
        </div>
        <div style={styles.steps}>
          {steps.map((s) => (
            <div key={s.n} style={styles.step}>
              <div style={styles.stepNum}>{s.n}</div>
              <div style={styles.stepContent}>
                <div style={styles.stepLabel}>{s.label}</div>
                <div style={{ marginTop: 10 }}>{s.action}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Already installed hint ── */}
      {status === "not-running" && (
        <div style={styles.hintBox}>
          <span style={{ fontSize: 18 }}>💡</span>
          <span>
            <strong style={{ color: "#fcd34d" }}>Already installed?</strong>{" "}
            {os === "mac"
              ? <>Look for the Studio icon in your <strong>menu bar</strong> (top-right). If it's not there, open it from Applications or Spotlight. Then click <strong>↺ Recheck</strong> above.</>
              : <>Look for the tray icon in the <strong>bottom-right taskbar</strong>. If not there, launch it from the Start Menu. Then click <strong>↺ Recheck</strong> above.</>
            }
          </span>
        </div>
      )}

      {/* ── Divider ── */}
      <div style={styles.divider} />

      {/* ── Benefits ── */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <span style={styles.sectionBadge}>FEATURES</span>
          <h3 style={styles.sectionTitle}>Why install it?</h3>
        </div>
        <div style={styles.benefitsGrid}>
          {[
            { icon: "🎬", title: "Any file size",   desc: "Compress 2 GB, 4 GB, even 10 GB videos — no browser limits or timeouts." },
            { icon: "⚡", title: "4–5× faster",     desc: "Native FFmpeg vs browser WASM — dramatically faster encoding on any hardware." },
            { icon: "🎯", title: "Better quality",  desc: "1080p output with optimised preset — perfect for party sharing." },
            { icon: "🔒", title: "Fully local",     desc: "Video never leaves your machine during compression — private by design." },
          ].map((b) => (
            <div key={b.title} style={styles.benefitCard}>
              <div style={styles.benefitIcon}>{b.icon}</div>
              <strong style={styles.benefitTitle}>{b.title}</strong>
              <p style={styles.benefitDesc}>{b.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

const styles = {
  page: { maxWidth: 800, margin: "0 auto", padding: "32px 8px 56px", color: "#000" },

  /* Hero */
  hero: {
    background: "linear-gradient(135deg, rgba(124,58,237,0.22) 0%, rgba(124,58,237,0.06) 100%)",
    border: "1px solid rgba(124,58,237,0.28)",
    borderRadius: 16,
    padding: "28px 32px",
    marginBottom: 32,
  },
  heroLeft:    { display: "flex", alignItems: "flex-start", gap: 20 },
  heroIconWrap:{ fontSize: 44, lineHeight: 1, flexShrink: 0, marginTop: 2 },
  heroTitle:   { fontSize: 24, fontWeight: 800, color: "#000", margin: "0 0 8px", letterSpacing: "-0.3px" },
  heroSub:     { fontSize: 14, color: "#000", margin: "0 0 16px", lineHeight: 1.7, maxWidth: 620 },
  bannerCtaWrap: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, marginBottom: 18 },
  primaryDownloadBtn: { display: "inline-flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg,#7C3AED,#9333ea)", color: "#fff", padding: "11px 24px", borderRadius: 9, fontSize: 13.5, fontWeight: 700, textDecoration: "none", boxShadow: "0 3px 14px rgba(124,58,237,0.4)", letterSpacing: "0.01em" },
  downloadNote: { fontSize: 12.5, color: "#000", lineHeight: 1.6, maxWidth: 640 },
  ffmpegNote: { fontSize: 12.5, color: "#000", lineHeight: 1.6, maxWidth: 640 },

  /* Status pill */
  statusPill:  { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, padding: "10px 16px", borderRadius: 10, border: "1px solid" },
  statusDotCircle: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  statusLabel: { fontWeight: 700, fontSize: 13.5 },
  statusSub:   { fontSize: 12.5, color: "#000", flex: 1 },
  recheckBtn:  { flexShrink: 0, background: "rgba(255,255,255,0.8)", border: "1px solid rgba(0,0,0,0.2)", color: "#000", borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 12.5, whiteSpace: "nowrap" },

  /* Sections */
  section:       { marginBottom: 36 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
  sectionBadge:  { fontSize: 10, fontWeight: 700, color: "#000", background: "rgba(255,255,255,0.7)", padding: "3px 8px", borderRadius: 4, letterSpacing: "0.08em", border: "1px solid rgba(0,0,0,0.15)" },
  sectionTitle:  { fontSize: 16, fontWeight: 700, color: "#000", margin: 0 },

  /* Steps */
  steps:       { display: "flex", flexDirection: "column", gap: 10 },
  step:        { display: "flex", alignItems: "flex-start", gap: 18, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "20px 24px", transition: "border-color 0.2s" },
  stepNum:     { width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#7C3AED,#9333ea)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0, boxShadow: "0 2px 10px rgba(124,58,237,0.45)", marginTop: 2 },
  stepContent: { flex: 1 },
  stepLabel:   { fontSize: 15, fontWeight: 600, color: "#000" },
  stepNote:    { fontSize: 13, color: "#000", lineHeight: 1.6 },
  downloadBtn: { display: "inline-flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg,#7C3AED,#9333ea)", color: "#fff", padding: "11px 24px", borderRadius: 9, fontSize: 13.5, fontWeight: 700, textDecoration: "none", boxShadow: "0 3px 14px rgba(124,58,237,0.4)", letterSpacing: "0.01em" },

  /* Hint */
  hintBox: { display: "flex", alignItems: "flex-start", gap: 14, background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 12, padding: "16px 20px", fontSize: 13.5, color: "#000", lineHeight: 1.65, marginBottom: 32 },

  /* Update banner */
  updateBanner: { display: "flex", alignItems: "center", gap: 14, background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.5)", borderRadius: 12, padding: "16px 20px", marginBottom: 28, flexWrap: "wrap" },
  updateBtn:    { flexShrink: 0, background: "linear-gradient(135deg,#d97706,#b45309)", color: "#fff", padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" },

  /* Divider */
  divider: { borderTop: "1px solid rgba(255,255,255,0.06)", marginBottom: 36 },

  /* Benefits */
  benefitsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  benefitCard:  { background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "20px 22px" },
  benefitIcon:  { fontSize: 28, marginBottom: 12 },
  benefitTitle: { fontSize: 14, color: "#000", display: "block", marginBottom: 7, fontWeight: 700 },
  benefitDesc:  { fontSize: 13, color: "#000", margin: 0, lineHeight: 1.6 },
};

export default CompanionInstall;
